/**
 * Campaign list (task 004-05).
 *
 * Rewritten to align with admin-requirements.md §6 (Jul-29 review call + Jul-24 conventions).
 *
 * Key changes from epic-002:
 * - Two verbatim column orders (Co-op vs Custom Opt-in)
 * - Advertiser-primary rows with campaign name beneath
 * - Slug + copy-full-URL for hosted-page campaigns
 * - Keyword search over rendered text including month names
 * - MM-DD-YYYY dates
 * - Inline Pause/Complete actions
 * - "Co-op" terminology everywhere user-visible
 * - Campaign style picker replaces bare flowKind
 * - Five saved views: Co-op, Custom Opt-in, Paid Social, CTO Email, All
 * - Clickable A-Z sort on Advertiser and Client Name (task 023-06, HF-111); the Page Title
 *   column and its sort were removed in every view (HF-138)
 */

import { useEffect, useState, useMemo, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, type CampaignDetailResponse } from '../lib/api';
import { AdvertiserResponse, CampaignResponse, CampaignStatus } from '@leads-platform/shared';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { PageHeader } from '../components/ui/page-header';
import { SearchInput } from '../components/ui/search-input';
import { Select } from '../components/ui/select';
import { Typeahead } from '../components/typeahead';
import { Tag } from '../components/ui/tag';
import { StatusPill } from '../components/ui/status-pill';
import { CopyIconButton, writeClipboard } from '../components/ui/copy-button';
import { useToast } from '../components/ui/toast';
import { fmtDate, monthName, fmtCount, fmtMoney } from '../lib/format';
import {
  pageUrl,
  CAMPAIGN_STYLES,
  styleToTone,
  CampaignStyle,
  campaignStyleEnumToLabel,
} from '../lib/campaign-styles';

// Enriched campaign type with advertiser name and style
type EnrichedCampaign = CampaignDetailResponse & {
  advertiserName: string;
  style: CampaignStyle;
};

/**
 * User-triggered column sort (task 023-06), layered over the task-05
 * most-recent-first API default: `null` = no user sort, rows render in API
 * order. Both arrows show in the header when no sort is active. HF-111 adds
 * the Client Name column to the sortable set (QA-23). HF-138 removes the Page
 * Title column and its sort in every view (QA-30..33).
 */
type SortKey = 'advertiserName' | 'clientName';
type SortDir = 'asc' | 'desc';
type CampaignSort = { key: SortKey; dir: SortDir };

/**
 * View configuration - the five saved views.
 * Each view maps to a campaign-style filter and determines columns.
 */
const VIEWS = [
  { key: 'coop', label: 'Co-op', style: 'Co-op' },
  { key: 'optin', label: 'Custom Opt-in', style: 'Custom Opt-in' },
  { key: 'paid_social', label: 'Paid Social', style: 'Paid Social' },
  { key: 'cto_email', label: 'CTO Email', style: 'CTO Email' },
  { key: 'all', label: 'All', style: null },
] as const;

type ViewKey = (typeof VIEWS)[number]['key'];

/**
 * Status options for filter.
 */
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'test', label: 'Test' },
];

/**
 * Search over rendered row text: advertiser, campaign name, client name, slug, dates, month names
 * (no page title). Client name stays searchable in every view, including those whose Client Name
 * column is hidden.
 * Per admin-requirements.md §1.4 - keyword search matches rendered text including month names.
 */
function searchBlob(c: EnrichedCampaign): string {
  return [
    c.advertiserName || '',
    c.name,
    c.clientName,
    c.pageSlug,
    fmtDate(c.startDate),
    fmtDate(c.endDate),
    monthName(c.startDate),
    monthName(c.endDate),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Advertiser cell with campaign name beneath (advertiser-primary pattern).
 */
function AdvertiserCell({ c }: { c: EnrichedCampaign }) {
  return (
    <Link to={`/campaigns/${c.id}`} onClick={(e) => e.stopPropagation()} className="block">
      <span
        className="block truncate text-left font-semibold text-ink hover:text-accent transition-colors"
        title={c.advertiserName}
      >
        {c.advertiserName}
      </span>
      <div className="truncate text-xs text-faint" title={c.name}>
        {c.name}
      </div>
    </Link>
  );
}

/**
 * Site IDs cell - shows first 2 with "+N" overflow indicator.
 */
function SiteIdCell({ ids }: { ids: string[] }) {
  if (!ids || ids.length === 0) return <span className="text-faint">--</span>;

  const shown = ids.slice(0, 2);
  return (
    <span className="font-mono text-xs text-muted" title={ids.join(', ')}>
      {shown.join(', ')}
      {ids.length > shown.length && (
        <span className="text-faint"> +{ids.length - shown.length}</span>
      )}
    </span>
  );
}

/**
 * Client Name cell - the campaign's free-text clientName (task 023-06),
 * display-only: truncated to 20 chars with the full value on hover; `--` when
 * null/empty.
 */
function ClientNameCell({ clientName }: { clientName: string | null | undefined }) {
  if (!clientName) return <span className="text-faint">--</span>;
  return (
    <span className="text-muted" title={clientName}>
      {clientName.length > 20 ? `${clientName.slice(0, 20)}...` : clientName}
    </span>
  );
}

/**
 * Suppression cell (HF-069, QA-08): a suppression only "applies" when the
 * row is `enabled` AND carries a file or a non-empty (post-`trim()`)
 * pre-ping URL — the worker's own pre-ping gate
 * (packages/api/src/modules/distribution/rules/pre-ping.rule.ts:31). An
 * `enabled` row with neither (the legacy toggle default, campaign 32's
 * shape) is `--`, same as no row or a disabled one. File and pre-ping are
 * independent and both can show; tone stays sky.
 */
function SuppressionCell({ suppression }: { suppression: CampaignResponse['suppression'] }) {
  const hasFile = Boolean(suppression?.enabled && suppression.suppressionFileId);
  const hasPrePing = Boolean(suppression?.enabled && suppression.prepingApiUrl?.trim());

  if (!hasFile && !hasPrePing) return <span className="text-faint">--</span>;

  return (
    <span className="inline-flex flex-wrap items-center justify-center gap-1">
      {hasFile && <Tag tone="sky">File</Tag>}
      {hasPrePing && <Tag tone="sky">Pre-ping</Tag>}
    </span>
  );
}

/**
 * Sortable column header (task 023-06): clicking cycles none → asc → desc →
 * none. The active column carries `aria-sort` and a single direction glyph;
 * inactive sortable headers show BOTH arrows — the operator's "sort is off"
 * affordance (operator edit 2026-09-02). The glyphs are aria-hidden so the
 * accessible name stays the bare column label.
 */
function SortableHeader({
  label,
  sortKey,
  sort,
  onToggle,
  title,
}: {
  label: string;
  sortKey: SortKey;
  sort: CampaignSort | null;
  onToggle: (key: SortKey) => void;
  /** Optional column-help tooltip, rendered as the `<th>`'s `title`. */
  title?: string;
}) {
  const active = sort !== null && sort.key === sortKey;
  const dir: SortDir | null = active && sort ? sort.dir : null;

  return (
    <th
      className="px-1.5 py-2 font-semibold whitespace-nowrap"
      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined}
      title={title}
    >
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className="inline-flex cursor-pointer items-center gap-1 transition-colors hover:text-ink"
      >
        {label}
        <div
          aria-hidden="true"
          className={dir ? '' : 'text-faint inline-flex flex-col'}
          title={dir ? undefined : 'Sort is off — click to sort A-Z'}
        >
          <span>{dir === 'desc' ? '▼' : '▲'}</span>
          <span className="-mt-1">{!dir ? '▼' : ''}</span>
        </div>
      </button>
    </th>
  );
}

/**
 * Inline external-link icon (no icon library — task 021-09 ruling #6).
 */
const OpenPageIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

/**
 * Slug cell for hosted-page campaigns - one uniform design on every view
 * carrying hosted-page rows (task 021-09, bug #30 operator ruling): a bordered
 * mono chip showing the slug truncated to 12 chars, whose click copies the
 * full page URL without triggering the row click, plus a copy-icon button
 * with the same action and visible copied feedback, plus an open-link icon
 * button (task 037-08) that opens the published page in a new tab without
 * triggering the row click. `--` when the campaign has no hosted page (e.g.
 * a Co-op row in the All view) — and for CTO Email rows even when they carry
 * the auto-generated pageSlug, because they have no hosted page by design
 * (task 045-03); the slug stays load-bearing and searchable, it is simply not
 * displayed (QA #72, ruling 2026-09-14).
 */
function SlugCell({ c }: { c: CampaignResponse }) {
  const url = pageUrl(c.pageSlug);
  if (!url || !c.pageSlug || c.campaignStyle === 'cto_email')
    return <span className="text-faint">--</span>;

  const truncatedSlug = c.pageSlug.length > 12 ? `${c.pageSlug.slice(0, 12)}...` : c.pageSlug;

  return (
    <span className="flex items-center gap-1">
      <button
        type="button"
        title={`${c.pageSlug}: click to copy url`}
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await writeClipboard(url);
        }}
        className="rounded-md border border-line bg-paper px-1.5 py-0.5 font-mono text-xs text-muted transition hover:bg-accent-soft hover:text-accent"
      >
        {truncatedSlug}
      </button>
      <CopyIconButton value={url} />
      <button
        type="button"
        aria-label="Open page in new tab"
        title="Open page in new tab"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          window.open(url, '_blank', 'noopener');
        }}
        className="shrink-0 rounded-md p-1 transition bg-line/60 text-muted hover:bg-accent-soft hover:text-accent"
      >
        <OpenPageIcon />
      </button>
    </span>
  );
}

/**
 * Sub-label map for statusReason values (ADR-010 §3 / A16). `schedule` and
 * null are absent — the pill already communicates those states. `cap_total`
 * and `end_date` annotate a Completed pill; `manual` annotates a Pending pill.
 */
const STATUS_REASON_LABELS: Partial<Record<NonNullable<CampaignResponse['statusReason']>, string>> =
  {
    cap_total: 'Completed — cap hit',
    end_date: 'Completed — end date',
    manual: 'Paused by admin',
  };

/**
 * Sub-label map for an archived campaign's statusReason (task 064-04, QA-02,
 * D8). A reason outside this map, or none, falls back to plain "Archived".
 */
const ARCHIVED_REASON_LABELS: Partial<
  Record<NonNullable<CampaignResponse['statusReason']>, string>
> = {
  cap_total: 'Archived — cap hit',
  end_date: 'Archived — end date',
};

/**
 * Status cell: pill + optional sub-label for worker- and admin-set reasons.
 * An archived campaign (task 064-04) shows its archived sub-label instead;
 * the pill itself is unaffected — it still reads the underlying `status`,
 * which stays Completed while archived (D3).
 */
function StatusCell({ c }: { c: CampaignDetailResponse }) {
  const status = c.status as CampaignStatus;
  const archived = c.archivedAt != null;
  const subLabel = archived
    ? c.statusReason != null
      ? (ARCHIVED_REASON_LABELS[c.statusReason] ?? 'Archived')
      : 'Archived'
    : c.statusReason != null
      ? (STATUS_REASON_LABELS[c.statusReason] ?? null)
      : null;
  return (
    <div>
      <StatusPill value={status} />
      {subLabel != null && <div className="text-xs text-faint">{subLabel}</div>}
    </div>
  );
}

/**
 * Row-action button (task 064-03, QA-01): a local ghost-button lookalike
 * instead of the shared `Button` component. `cn` in `lib/utils.ts` is plain
 * `clsx` with no tailwind-merge, so a `className` override on `Button` cannot
 * shrink it — its `sm` size (`h-9 px-3`) always wins over a `px-2` override
 * because the compiled CSS order decides, not JSX order. This renders at the
 * true compact size (`h-7`, 13px text) instead.
 */
function RowActionButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-7 whitespace-nowrap rounded-md px-1.5 text-xs font-semibold text-muted transition-colors hover:bg-line/50 hover:text-ink"
    >
      {children}
    </button>
  );
}

// Mirrors campaign-form.tsx `primaryButtonState`: a Pending campaign whose start
// date (date part, read as UTC midnight like the form's DATE_TO_MS) is still
// ahead is scheduled, not resumed. Returns that instant, or undefined otherwise.
function futureStartInstant(startDate: string | undefined): Date | undefined {
  if (!startDate) return undefined;
  const start = new Date(`${startDate.slice(0, 10)}T00:00:00Z`);
  return start.getTime() > Date.now() ? start : undefined;
}

type RowAction = 'pause' | 'complete' | 'resume' | { kind: 'schedule'; scheduleDate: string };

/**
 * Row actions - inline Pause/Complete buttons.
 */
function RowActions({
  c,
  onAction,
  onReopen,
  onArchive,
  onUnarchive,
}: {
  c: CampaignDetailResponse;
  onAction: (id: string, action: RowAction) => void;
  onReopen?: (id: string, name: string) => void;
  onArchive?: (id: string, name: string) => void;
  onUnarchive?: (id: string, name: string) => void;
}) {
  const status = c.status as CampaignStatus;
  const done = status === 'completed';
  const archived = Boolean(c.archivedAt);

  // Can pause: Active, Scheduled, Test
  const canPause = status === 'active' || status === 'scheduled' || status === 'test';
  // Can resume: Pending
  const canResume = status === 'pending';
  // Pending with a future start: Schedule replaces Resume (HF-115, QA-21)
  const scheduleAt = canResume ? futureStartInstant(c.startDate) : undefined;
  // Can complete: anything not already completed
  const canComplete = !done;
  // Can reopen: Completed and not archived (task 064-02, QA-17; task 064-04, QA-02, D3)
  const canReopen = done && !archived;
  // Can archive: Completed and not already archived (task 064-04, QA-02, D2)
  const canArchive = done && !archived;

  return (
    <div className="flex items-center justify-end gap-1">
      {/* A real link (HF-109) so Clone can open in a new tab; the cell's
          onClick stopPropagation keeps the row's click-through from firing. */}
      <Link
        to={`/campaigns/${c.id}/duplicate`}
        className="inline-flex h-7 items-center whitespace-nowrap rounded-md px-1.5 text-xs font-semibold text-muted transition-colors hover:bg-line/50 hover:text-ink"
      >
        Clone
      </Link>
      {canResume &&
        (scheduleAt ? (
          <RowActionButton
            onClick={() =>
              onAction(c.id, { kind: 'schedule', scheduleDate: scheduleAt.toISOString() })
            }
          >
            Schedule
          </RowActionButton>
        ) : (
          <RowActionButton onClick={() => onAction(c.id, 'resume')}>Resume</RowActionButton>
        ))}
      {canPause && <RowActionButton onClick={() => onAction(c.id, 'pause')}>Pause</RowActionButton>}
      {canComplete && (
        <RowActionButton onClick={() => onAction(c.id, 'complete')}>Complete</RowActionButton>
      )}
      {canReopen && onReopen && (
        <RowActionButton onClick={() => onReopen(c.id, c.name)}>Reopen</RowActionButton>
      )}
      {canArchive && onArchive && (
        <RowActionButton onClick={() => onArchive(c.id, c.name)}>Archive</RowActionButton>
      )}
      {archived && onUnarchive && (
        <RowActionButton onClick={() => onUnarchive(c.id, c.name)}>Unarchive</RowActionButton>
      )}
    </div>
  );
}

export function Campaigns() {
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();

  const [view, setView] = useState<ViewKey>('all');
  const [q, setQ] = useState('');
  const [advertiserFilter, setAdvertiserFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [styleFilter, setStyleFilter] = useState('');
  const [sort, setSort] = useState<CampaignSort | null>(null);

  const [campaigns, setCampaigns] = useState<CampaignDetailResponse[]>([]);
  const [advertisers, setAdvertisers] = useState<AdvertiserResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Load advertisers
  useEffect(() => {
    api
      .getAdvertisers()
      .then(setAdvertisers)
      .catch(() => {
        // Best-effort: advertiser filter just stays empty
      });
  }, []);

  // Load campaigns
  const loadCampaigns = () => {
    api
      .getCampaigns()
      .then(setCampaigns)
      .catch((err: ApiError) => setError(err.message));
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  // Build advertiser lookup map
  const advertiserMap = useMemo(() => {
    const map = new Map<string, string>();
    advertisers.forEach((a) => map.set(a.id, a.name));
    return map;
  }, [advertisers]);

  // Enrich campaigns with advertiser names for search and display
  const enrichedCampaigns: EnrichedCampaign[] = useMemo(() => {
    return campaigns.map((c) => {
      // Read campaignStyle from response (server backfilled all rows)
      // Fall back to deriving from flowKind for any legacy data
      const style = c.campaignStyle
        ? campaignStyleEnumToLabel(c.campaignStyle)
        : c.flowKind === 'coreg'
          ? 'Co-op'
          : 'Custom Opt-in';
      return {
        ...c,
        advertiserName: advertiserMap.get(c.advertiserId) || 'Unknown',
        style,
      };
    });
  }, [campaigns, advertiserMap]);

  // Archived campaigns are hidden from every view by default (task 064-04,
  // QA-02, D8); `showArchived` brings them back. Downstream reads
  // (advertiser dropdown, row filter, footer total) use this, not
  // `enrichedCampaigns`, so hidden means hidden everywhere.
  const listCampaigns = useMemo(
    () =>
      showArchived ? enrichedCampaigns : enrichedCampaigns.filter((c) => c.archivedAt == null),
    [enrichedCampaigns, showArchived]
  );

  // Count of archived campaigns matching the current view's style, for the
  // "Show archived (N)" checkbox label (task 064-04, QA-02, D8).
  const archivedInView = useMemo(() => {
    const activeView = VIEWS.find((v) => v.key === view);
    return enrichedCampaigns.filter(
      (c) => (!activeView?.style || c.style === activeView.style) && c.archivedAt != null
    ).length;
  }, [enrichedCampaigns, view]);

  // Get available advertisers in current view
  const advertisersInView = useMemo(() => {
    const activeView = VIEWS.find((v) => v.key === view);
    const names = listCampaigns
      .filter((c) => !activeView?.style || c.style === activeView.style)
      .map((c) => c.advertiserName);
    return [...new Set(names)].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [listCampaigns, view]);

  // Filter campaigns
  const filteredCampaigns = useMemo(() => {
    const activeView = VIEWS.find((v) => v.key === view);
    const needle = q.trim().toLowerCase();

    return listCampaigns.filter((c) => {
      // View filter (by style)
      if (activeView?.style && c.style !== activeView.style) return false;

      // Advertiser filter
      if (advertiserFilter && c.advertiserName !== advertiserFilter) return false;

      // Status filter
      if (statusFilter && c.status !== statusFilter) return false;

      // Style filter (only in "All" view)
      if (view === 'all' && styleFilter && c.style !== styleFilter) return false;

      // Keyword search over rendered text
      if (needle && !searchBlob(c).includes(needle)) return false;

      return true;
    });
  }, [listCampaigns, view, advertiserFilter, statusFilter, styleFilter, q]);

  // User-triggered A-Z sort (task 023-06) over the filtered rows; `null` keeps
  // the task-05 most-recent-first API order. Non-mutating: copy, then sort.
  // Case-insensitive collation; empty values sort last in both directions.
  const sortedCampaigns = useMemo(() => {
    if (!sort) return filteredCampaigns;
    const { key, dir } = sort;
    return [...filteredCampaigns].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      const cmp = av.localeCompare(bv, 'en', { sensitivity: 'base' });
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [filteredCampaigns, sort]);

  // Cycle none → asc → desc → none; a different column starts fresh at asc.
  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const switchView = (key: ViewKey) => {
    setView(key);
    setAdvertiserFilter('');
    setStyleFilter('');
    setSort(null);
  };

  const reset = () => {
    setQ('');
    setAdvertiserFilter('');
    setStatusFilter('');
    setStyleFilter('');
    setSort(null);
    setShowArchived(false);
  };

  const handleAction = async (id: string, action: RowAction) => {
    try {
      if (typeof action === 'object') {
        await api.scheduleCampaign(id, action.scheduleDate);
        success('Campaign scheduled');
      } else if (action === 'pause') {
        await api.pauseCampaign(id);
        success('Campaign paused');
      } else if (action === 'resume') {
        await api.resumeCampaign(id);
        success('Campaign resumed');
      } else if (action === 'complete') {
        await api.completeCampaign(id);
        success('Campaign completed');
      }
      loadCampaigns();
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Action failed');
    }
  };

  const handleReopen = async (id: string, name: string) => {
    if (!confirm(`Reopen '${name}'? It goes live again and starts receiving leads.`)) return;
    try {
      await api.reopenCampaign(id);
      success('Campaign reopened');
      loadCampaigns();
    } catch (err) {
      const message = err instanceof Error ? err.message : undefined;
      toastError(message || 'Reopen failed');
    }
  };

  const handleArchive = async (id: string, name: string) => {
    if (
      !confirm(
        `Archive '${name}'? It leaves the campaign list. Its leads and stats are kept, and its page and links work as before.`
      )
    )
      return;
    try {
      await api.archiveCampaign(id);
      success('Campaign archived');
      loadCampaigns();
    } catch (err) {
      const message = err instanceof Error ? err.message : undefined;
      toastError(message || 'Archive failed');
    }
  };

  const handleUnarchive = async (id: string, name: string) => {
    if (!confirm(`Unarchive '${name}'? It returns to the campaign list as Completed.`)) return;
    try {
      await api.unarchiveCampaign(id);
      success('Campaign unarchived');
      loadCampaigns();
    } catch (err) {
      const message = err instanceof Error ? err.message : undefined;
      toastError(message || 'Unarchive failed');
    }
  };

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading campaigns: {error}
        </div>
      </div>
    );
  }

  const activeView = VIEWS.find((v) => v.key === view);

  // View-dependent columns of the Co-op / Paid Social / CTO Email / All table (HF-138), and the
  // empty row's colSpan: nine columns every view carries (Advertiser, Date Range, Limit, Price,
  // Daily Cap, Suppression, Restricted IDs, Status, Actions) plus the optional ones.
  const showClientName = view !== 'coop' && view !== 'cto_email';
  const showSlug = view !== 'coop';
  const listColSpan = 9 + (showClientName ? 1 : 0) + (view === 'all' ? 1 : 0) + (showSlug ? 1 : 0);

  return (
    <div>
      <PageHeader
        kicker="Campaigns"
        title="Campaigns"
        actions={<Button onClick={() => navigate('/campaigns/new')}>+ Add campaign</Button>}
      />

      {/* View switcher */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg bg-paper p-0.5 hairline">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => switchView(v.key)}
              className={`rounded-md px-3.5 py-1.5 text-xs font-semibold transition ${
                view === v.key ? 'bg-accent text-white shadow-sm' : 'text-muted hover:text-ink'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-faint">
          Five saved views over one model — the view is the campaign-style filter
        </span>
      </div>

      {/* Filters */}
      <Card className="mb-6 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
          <SearchInput
            value={q}
            onChange={setQ}
            placeholder="Search advertiser, campaign, slug or month (e.g. July)"
            className="min-w-[420px]"
          />

          <Typeahead<string>
            options={advertisersInView}
            value={advertiserFilter || null}
            onChange={(v) => setAdvertiserFilter(v ?? '')}
            getId={(a) => a}
            getLabel={(a) => a}
            allOption={{ label: 'All advertisers' }}
            className="min-w-[170px]"
          />

          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 min-w-[140px]"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>

          {view === 'all' && (
            <Select
              value={styleFilter}
              onChange={(e) => setStyleFilter(e.target.value)}
              className="h-9 min-w-[150px]"
            >
              <option value="">All styles</option>
              {CAMPAIGN_STYLES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
          )}

          <label
            className="inline-flex items-center gap-1.5 text-xs text-muted"
            title="Archive is on Completed campaigns: complete one first, then archive it"
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived ({archivedInView})
          </label>

          <Button variant="ghost" size="sm" className="ml-auto" onClick={reset}>
            Reset
          </Button>
        </div>

        {/* Campaign table */}
        <div className="overflow-x-auto">
          {view === 'optin' ? (
            // Custom Opt-in offer-page list - verbatim column order from §1.4
            // (+ Client Name after Advertiser, task 023-06; no Page Title, HF-138)
            <table className="w-full whitespace-nowrap text-left text-xs">
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-faint">
                  <SortableHeader
                    label="Advertiser"
                    sortKey="advertiserName"
                    sort={sort}
                    onToggle={toggleSort}
                  />
                  <SortableHeader
                    label="Client"
                    title="Client Name"
                    sortKey="clientName"
                    sort={sort}
                    onToggle={toggleSort}
                  />
                  <th className="px-1.5 py-2 font-semibold whitespace-nowrap">Date Range</th>
                  <th className="px-1.5 py-2 text-right font-semibold whitespace-nowrap">
                    Limit Type
                  </th>
                  <th className="px-1.5 py-2 text-right font-semibold whitespace-nowrap">Limit</th>
                  <th className="px-1.5 py-2 text-right font-semibold whitespace-nowrap">Cost</th>
                  <th className="px-1.5 py-2 text-right font-semibold whitespace-nowrap">
                    Daily Cap
                  </th>
                  <th className="px-1.5 py-2 font-semibold whitespace-nowrap">Restricted IDs</th>
                  <th className="px-1.5 py-2 font-semibold whitespace-nowrap">Status</th>
                  <th className="px-1.5 py-2 font-semibold whitespace-nowrap">slug</th>
                  <th className="px-1.5 py-2 text-right font-semibold whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-faint">
                      No campaigns match this view and filter combination.
                    </td>
                  </tr>
                ) : (
                  sortedCampaigns.map((c) => (
                    <tr
                      key={c.id}
                      className={`border-b border-line/70 transition hover:bg-paper/60 cursor-pointer${c.archivedAt ? ' opacity-60' : ''}`}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                        navigate(`/campaigns/${c.id}`);
                      }}
                    >
                      <td className="w-[24%] min-w-[11rem] max-w-0 px-1.5 py-1.5">
                        <AdvertiserCell c={c} />
                      </td>
                      <td className="px-1.5 py-1.5">
                        <ClientNameCell clientName={c.clientName} />
                      </td>
                      <td className="px-1.5 py-1.5 font-mono text-xs text-muted whitespace-nowrap">
                        {fmtDate(c.startDate)}
                        <br />
                        {fmtDate(c.endDate)}
                      </td>
                      <td className="px-1.5 py-1.5 text-right">{c.limitType || '--'}</td>
                      <td className="px-1.5 py-1.5 text-right">{fmtCount(c.limitValue)}</td>
                      <td className="px-1.5 py-1.5 text-right">{fmtMoney(c.pricePerLead)}</td>
                      <td className="px-1.5 py-1.5 text-right">{fmtCount(c.dailyCap)}</td>
                      <td className="px-1.5 py-1.5">
                        <SiteIdCell ids={c.siteRestrictions?.map((sr) => sr.siteId) || []} />
                      </td>
                      <td className="px-1.5 py-1.5">
                        <StatusCell c={c} />
                      </td>
                      <td className="px-1.5 py-1.5">
                        <SlugCell c={c} />
                      </td>
                      <td className="px-1.5 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <RowActions
                          c={c}
                          onAction={handleAction}
                          onReopen={handleReopen}
                          onArchive={handleArchive}
                          onUnarchive={handleUnarchive}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            // Co-op / Paid Social / CTO Email / All - verbatim column order from §1.4
            // (+ Client Name after Advertiser, task 023-06). Co-op carries Restricted IDs
            // again (QA-29, reversing the 2026-09-02 operator edit); Client Name is off
            // Co-op and CTO Email (QA-31/33); no Page Title in any view (QA-30).
            <table className="w-full whitespace-nowrap text-left text-xs">
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-faint">
                  <SortableHeader
                    label="Advertiser"
                    sortKey="advertiserName"
                    sort={sort}
                    onToggle={toggleSort}
                  />
                  {showClientName && (
                    <SortableHeader
                      label="Client"
                      title="Client Name"
                      sortKey="clientName"
                      sort={sort}
                      onToggle={toggleSort}
                    />
                  )}
                  {view === 'all' && (
                    <th className="px-1.5 py-2 font-semibold whitespace-nowrap">Style</th>
                  )}
                  <th className="px-1.5 py-2 font-semibold whitespace-nowrap">Date Range</th>
                  <th className="px-1.5 py-2 text-right font-semibold whitespace-nowrap">Limit</th>
                  <th className="px-1.5 py-2 text-right font-semibold whitespace-nowrap">Price</th>
                  <th className="px-1.5 py-2 text-right font-semibold whitespace-nowrap">
                    {view === 'coop' ? 'Daily Lead Cap' : 'Daily Cap'}
                  </th>
                  <th
                    className="px-1.5 py-2 text-center font-semibold whitespace-nowrap"
                    title="Suppression"
                  >
                    Supr.
                  </th>
                  <th className="px-1.5 py-2 font-semibold whitespace-nowrap">Restricted IDs</th>
                  <th className="px-1.5 py-2 font-semibold whitespace-nowrap">Status</th>
                  {showSlug && (
                    <th className="px-1.5 py-2 font-semibold whitespace-nowrap">slug</th>
                  )}
                  <th className="px-1.5 py-2 text-right font-semibold whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.length === 0 ? (
                  <tr>
                    <td colSpan={listColSpan} className="px-4 py-8 text-center text-faint">
                      No campaigns match this view and filter combination.
                    </td>
                  </tr>
                ) : (
                  sortedCampaigns.map((c) => (
                    <tr
                      key={c.id}
                      className={`border-b border-line/70 transition hover:bg-paper/60 cursor-pointer${c.archivedAt ? ' opacity-60' : ''}`}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                        navigate(`/campaigns/${c.id}`);
                      }}
                    >
                      <td className="w-[24%] min-w-[11rem] max-w-0 px-1.5 py-1.5">
                        <AdvertiserCell c={c} />
                      </td>
                      {showClientName && (
                        <td className="px-1.5 py-1.5">
                          <ClientNameCell clientName={c.clientName} />
                        </td>
                      )}
                      {view === 'all' && (
                        <td className="whitespace-normal px-1.5 py-1.5">
                          <Tag tone={styleToTone(c.style)}>{c.style}</Tag>
                        </td>
                      )}
                      <td className="px-1.5 py-1.5 font-mono text-xs text-muted whitespace-nowrap">
                        {fmtDate(c.startDate)}
                        <br />
                        {fmtDate(c.endDate)}
                      </td>
                      <td className="px-1.5 py-1.5 text-right">{fmtCount(c.limitValue)}</td>
                      <td className="px-1.5 py-1.5 text-right">{fmtMoney(c.pricePerLead)}</td>
                      <td className="px-1.5 py-1.5 text-right">{fmtCount(c.dailyCap)}</td>
                      <td className="px-1.5 py-1.5 text-center">
                        <SuppressionCell suppression={c.suppression} />
                      </td>
                      <td className="px-1.5 py-1.5">
                        <SiteIdCell ids={c.siteRestrictions?.map((sr) => sr.siteId) || []} />
                      </td>
                      <td className="px-1.5 py-1.5">
                        <StatusCell c={c} />
                      </td>
                      {showSlug && (
                        <td className="px-1.5 py-1.5">
                          <SlugCell c={c} />
                        </td>
                      )}
                      <td className="px-1.5 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <RowActions
                          c={c}
                          onAction={handleAction}
                          onReopen={handleReopen}
                          onArchive={handleArchive}
                          onUnarchive={handleUnarchive}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <p className="mt-3 font-mono text-xs text-faint">
        {sortedCampaigns.length} of {listCampaigns.length} campaigns · view: {activeView?.label} ·
        dates MM-DD-YYYY
      </p>
    </div>
  );
}
